import { useEffect, useRef, useState } from "react";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { fetchProviderFromNdh } from "../utils/ndhLookup";
import LoadingIndicator from "../utils/LoadingIndicator";
import classes from "./style.module.scss";
import NpdRecordCards from "./NpdRecordCards";
import OrganizationDetailsModal from "./OrganizationDetailsModal/OrganizationDetailsModal";

/**
 * NPD details tab — mirrors every section of the public NPD provider
 * page (directory.cms.gov/provider/{npi}), fed by fetchProviderFromNdh
 * (NPD search API first, FHIR for the care-team graph / fallback).
 *
 * This component only owns the fetch-by-npi lifecycle (loading / not-
 * found / error / retry) plus the organization-detail modal's open
 * state. The actual card grid (Summary, Practices and affiliations,
 * Care team, Endpoints, Insurance & network participation, Licenses
 * & registrations) is NpdRecordCards — shared with
 * OrganizationDetailsModal so a clicked affiliated organization
 * renders through the exact same code, not a second hand-maintained
 * copy of these cards.
 *
 * Every empty state uses the same wording the NPD page uses, so a
 * missing value reads as "the directory doesn't have it", not a bug.
 */
export default function NpdDetailsTab({ npi, fallbackName }) {
  const [status, setStatus] = useState("idle");
  const [data, setData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  // NPI of the organization currently open in the expanded-view modal
  // (see "Practices and affiliations" -> onOrgClick below). null = closed.
  const [openOrgNpi, setOpenOrgNpi] = useState(null);
  const cacheRef = useRef({});

  const load = () => {
    if (!npi) {
      setStatus("error");
      setErrorMessage("No NPI available for this provider.");
      return;
    }
    if (cacheRef.current[npi]) {
      setData(cacheRef.current[npi]);
      setStatus("success");
      return;
    }
    setStatus("loading");
    setErrorMessage("");
    fetchProviderFromNdh(npi)
      .then((result) => {
        cacheRef.current[npi] = result;
        setData(result);
        setStatus("success");
      })
      .catch((err) => {
        setStatus(err?.code === "NOT_FOUND" ? "not-found" : "error");
        setErrorMessage(err?.message || "Couldn't reach the provider directory.");
      });
  };

  useEffect(() => {
    load();
    setOpenOrgNpi(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npi]);

  if (status === "loading" || status === "idle") {
    return (
      // Same Modus compass loader used across the app, inline variant
      // (no full-screen overlay) so it sits inside the NPD tab panel.
      <div className={classes["npd-loading-block"]} role="status" aria-live="polite">
        <LoadingIndicator fullScreen={false} context="npd" height={72} />
      </div>
    );
  }

  if (status === "not-found" || status === "error") {
    return (
      <div className={classes["npd-state-block"]}>
        <p className={classes["npd-state-text"]}>{errorMessage}</p>
        <button type="button" className={classes["npd-retry-btn"]} onClick={load}>
          <RefreshRoundedIcon sx={{ fontSize: 15 }} />
          Try again
        </button>
        {npi && (
          <a
            className={classes["npd-fallback-link"]}
            href={`https://directory.cms.gov/provider/${npi}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open the NPD page instead
            <OpenInNewRoundedIcon sx={{ fontSize: 12 }} />
          </a>
        )}
      </div>
    );
  }

  return (
    <>
      <NpdRecordCards
        key={npi}
        data={data}
        npi={npi}
        fallbackName={fallbackName}
        onOrgClick={(org) => org.npi && setOpenOrgNpi(org.npi)}
      />
      <OrganizationDetailsModal
        open={!!openOrgNpi}
        orgNpi={openOrgNpi}
        onClose={() => setOpenOrgNpi(null)}
      />
    </>
  );
}